import { DatadogDevtools, DatadogProvider } from '@datadog/openfeature-browser';
import {
  OpenFeature,
  OpenFeatureEventEmitter,
  ProviderEvents,
  StandardResolutionReasons,
} from '@openfeature/web-sdk';
import type { JsonValue, Provider } from '@openfeature/web-sdk';
import { useSyncExternalStore } from 'react';
import exportToGlobalNamespace from '../exportToGlobalNamespace';
import {
  getDatadogApplicationId,
  getDatadogClientToken,
  getDatadogProxyOrigin,
  getMCOEnvironment,
} from '../metaTags';
import { classifyFailure, reportFlagInitFailure } from './transport';
import type { FlagTransportRoute } from './transport';

// The OpenFeature React hooks apps read flags with — use these directly for a flag's
// value, e.g. `useBooleanFlagValue('my_flag', false)`.
export {
  OpenFeatureProvider,
  useBooleanFlagValue,
  useStringFlagValue,
  useNumberFlagValue,
  useObjectFlagValue,
} from '@openfeature/react-sdk';

/** A flag's value type, matching its variation type in Datadog. */
export type FlagType = 'boolean' | 'string' | 'number' | 'object';

// Datadog org region — one value across all environments. Change if the org is
// not on US1 (e.g. 'us5.datadoghq.com', 'us3.datadoghq.com', 'datadoghq.eu').
const DATADOG_SITE = 'datadoghq.com';

/**
 * Imperative flag read for non-component / shared code — utils, or shared
 * `portal/libs` that may render in a provider-less host. Reads the global OpenFeature
 * client: no `<OpenFeatureProvider>` needed, never throws (returns the default when
 * no provider is registered), not reactive. The value type follows the `defaultValue`
 * you pass, so it works for any flag type and any key — including dynamic or
 * unregistered ones (the registry doesn't constrain it).
 *
 * @example
 * ```ts
 * if (getFlag('google_advanced_markers')) { ... }   // boolean (default false)
 * const color = getFlag('map-theme', 'blue');        // string
 * ```
 */
export function getFlag(flagKey: string, defaultValue?: boolean): boolean;
export function getFlag(flagKey: string, defaultValue: string): string;
export function getFlag(flagKey: string, defaultValue: number): number;
export function getFlag<T extends object>(flagKey: string, defaultValue: T): T;
export function getFlag(
  flagKey: string,
  defaultValue: boolean | string | number | object = false,
): boolean | string | number | object {
  const client = OpenFeature.getClient();
  switch (typeof defaultValue) {
    case 'string':
      return client.getStringValue(flagKey, defaultValue);
    case 'number':
      return client.getNumberValue(flagKey, defaultValue);
    case 'object':
      return client.getObjectValue(
        flagKey,
        defaultValue as unknown as JsonValue,
      ) as object;
    default:
      return client.getBooleanValue(flagKey, defaultValue as boolean);
  }
}

/**
 * A credential is usable only if it's non-empty and not an unresolved secrets
 * placeholder. Deployed stacks read config from `local_config`, where a key
 * missing from the env's secrets bundle leaves the literal `<loaded from
 * secrets>` string in place — the `<` check catches that.
 */
const isUsableCred = (value: string) => !!value && !value.includes('<');

/**
 * Normalizes `MCO_ENVIRONMENT` into a stable Datadog `env` tag. Deployed values
 * carry a cluster prefix and `-mco` suffix (`gaia-dev-mco`, `prime-dev-mco`,
 * `gaia-qa-alpha`, `gaia-tokyo`); we keep the segment after the first hyphen so
 * they collapse to clean tags — `dev`, `qa`, `staging`, `sandbox`, `ci`, `prod`,
 * `tokyo` — and cluster/region variants (`gaia-`/`prime-`, east/west) share one
 * tag. Single-token values (`local`) pass through; empty falls back to `local`.
 */
const toDatadogEnv = (mcoEnv: string) => {
  if (!mcoEnv) return 'local';
  const [first, second] = mcoEnv.split('-');
  return second || first;
};

/**
 * Inner provider used when Datadog creds are absent (devbox, CI, most test envs):
 * resolves everything to the caller's default. `DatadogDevtools` still wraps it, so
 * local overrides work in creds-less envs (e.g. Playwright forcing a flag). Carries
 * its own event emitter so override changes can be broadcast for live re-evaluation.
 */
const noopProvider: Provider = {
  metadata: { name: 'no-op' },
  events: new OpenFeatureEventEmitter(),
  resolveBooleanEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
  resolveStringEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
  resolveNumberEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
  resolveObjectEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
};

// The real DD provider (holds the fetched endpoint config) and the devtools wrapper
// (applies local overrides). Both null until initDatadogFlags runs.
let datadogProvider: DatadogProvider | null = null;
let devtoolsProvider: DatadogDevtools | null = null;

/**
 * Registers the Datadog Feature Flags provider on OpenFeature — the single place the
 * frontend wires up flags. Call once at an app root, before mounting
 * `<OpenFeatureProvider>`; components then read flags via {@link useFlag}.
 *
 * @remarks
 * Always installs {@link DatadogDevtools} so local overrides work everywhere. With
 * usable creds it wraps a real {@link DatadogProvider} (fetches config from the
 * endpoint); without them it wraps a no-op provider so flags fall back to defaults
 * rather than shipping a bogus token that Datadog 401s on. Per-environment on/off is
 * a Datadog dashboard targeting rule against `env` — the frontend never compares it.
 *
 * Whitelisting: DD's browser SDK is network-first, so ECC-safe delivery routes the
 * flag-config fetch through our own domain via `flaggingProxy` (the Amplitude
 * reverse-proxy pattern). With `DATADOG_PROXY_ORIGIN` set the browser only hits
 * `<origin>/_datadog-flags`; without it (devbox) it talks to DD directly. Exposure/
 * eval/RUM tracking is off, so the config fetch is the only browser→DD request.
 *
 * Stays synchronous — {@link ensureFlagsLoaded} awaits the fetch in the background.
 */
export const initDatadogFlags = () => {
  const clientToken = getDatadogClientToken();
  const applicationId = getDatadogApplicationId();

  let inner: Provider;
  let route: FlagTransportRoute | null = null;
  if (isUsableCred(clientToken) && isUsableCred(applicationId)) {
    const proxyOrigin = getDatadogProxyOrigin().replace(/\/$/, '');
    const flaggingProxy = proxyOrigin
      ? `${proxyOrigin}/_datadog-flags/precompute-assignments`
      : undefined;

    route = flaggingProxy ? 'proxy' : 'direct-to-datadog';

    datadogProvider = new DatadogProvider({
      clientToken,
      applicationId,
      site: DATADOG_SITE,
      env: toDatadogEnv(getMCOEnvironment()),
      ...(flaggingProxy ? { flaggingProxy } : {}),
      enableExposureLogging: false,
      enableFlagEvaluationTracking: false,
      enableRumFeatureFlagTracking: false,
    });
    inner = datadogProvider;
  } else {
    inner = noopProvider;
  }

  devtoolsProvider = new DatadogDevtools(inner);
  exposeFlagConsoleApi();

  // No route means the no-op provider: no fetch to fail, so nothing to report.
  if (route) {
    void ensureFlagsLoaded(devtoolsProvider, route);
  } else {
    OpenFeature.setProvider(devtoolsProvider);
  }
};

/** A flag as the Datadog endpoint reports it for the current environment. */
export interface ResolvedFlag {
  key: string;
  type: FlagType;
  value: boolean | string | number | object;
}

// Minimal read-side shape of the DD provider's fetched precompute config.
interface PrecomputedFlag {
  variationType: FlagType;
  variationValue: boolean | string | number | object;
}
type ProviderWithConfig = {
  flagsConfiguration?: {
    precomputed?: {
      response?: {
        data?: { attributes?: { flags?: Record<string, PrecomputedFlag> } };
      };
    };
  };
};

/**
 * The flags the Datadog endpoint actually returned for this environment — the real
 * list, not the code's registry. Empty when the endpoint is unreachable (no creds /
 * fetch not done), which is intentional: a flag not client-side-available for an env
 * won't appear, so half-built features can't leak into higher envs via the panel.
 */
export const getResolvedFlags = (): ResolvedFlag[] => {
  const flags = (datadogProvider as ProviderWithConfig | null)
    ?.flagsConfiguration?.precomputed?.response?.data?.attributes?.flags;
  if (!flags) return [];
  return Object.entries(flags)
    .map(([key, flag]) => ({
      key,
      type: flag.variationType,
      value: flag.variationValue,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
};

/**
 * Backoff between boot config fetch retries. The SDK has none of its own, so without
 * this one flaky fetch pins every flag to its read-site default for the session.
 */
const RETRY_DELAYS_MS = [1000, 4000];

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Awaits the boot config fetch, retrying before it reports.
 *
 * `setProviderAndWait` rather than `setProvider`: same code path, but `setProvider`
 * swallows the rejection into `console.error`, which captureConsole then turns into a
 * contextless Sentry event. Retries use `datadogProvider.onContextChange` rather than
 * `OpenFeature.setContext` for the same reason — it catches and logs.
 */
async function ensureFlagsLoaded(
  provider: DatadogDevtools,
  route: FlagTransportRoute,
): Promise<void> {
  const startedAt = Date.now();
  // Counted, not derived from the budget, so it holds when the loop exits early.
  let attempts = 1;
  let lastError: unknown;

  try {
    await OpenFeature.setProviderAndWait(provider);
    return;
  } catch (error) {
    lastError = error;
  }

  for (const delay of RETRY_DELAYS_MS) {
    // An abort means the SDK superseded its own request; nothing to recover.
    if (classifyFailure(lastError).kind === 'aborted') break;

    await sleep(delay);
    attempts += 1;
    try {
      const context = OpenFeature.getContext();
      await datadogProvider?.onContextChange(context, context);
      return;
    } catch (error) {
      lastError = error;
    }
  }

  reportFlagInitFailure({
    route,
    error: lastError,
    attempts,
    elapsedMs: Date.now() - startedAt,
  });
}

/**
 * Subscribes to flag-config changes (endpoint config ready, or a local override
 * toggled). Returns an unsubscribe fn. Lets the debug panel re-read
 * {@link getResolvedFlags} reactively without importing the vendor SDK.
 */
export const onFlagsChanged = (callback: () => void): (() => void) => {
  OpenFeature.addHandler(ProviderEvents.Ready, callback);
  OpenFeature.addHandler(ProviderEvents.ConfigurationChanged, callback);
  return () => {
    OpenFeature.removeHandler(ProviderEvents.Ready, callback);
    OpenFeature.removeHandler(ProviderEvents.ConfigurationChanged, callback);
  };
};

// A change token: useSyncExternalStore re-renders by comparing snapshots. Bumped
// inside subscribe so it's current before React re-reads, rather than depending on
// OpenFeature invoking handlers in registration order.
let flagsVersion = 0;

const subscribeToFlagChanges = (onStoreChange: () => void) =>
  onFlagsChanged(() => {
    flagsVersion += 1;
    onStoreChange();
  });

const getFlagsVersion = () => flagsVersion;

/**
 * Re-renders the caller when any flag changes. Pair it with an imperative
 * {@link getFlag} read, which has no subscription of its own; the typed
 * `use*FlagValue` hooks already have one.
 */
export const useFlagsVersion = (): number =>
  useSyncExternalStore(subscribeToFlagChanges, getFlagsVersion);

// ---- Local overrides (debug panel / console / E2E) -------------------------
// Backed by DatadogDevtools' localStorage contract so they're settable from the
// console or Playwright too. DatadogDevtools reads overrides once on init, so on
// every change we re-read them into the live provider and emit a change event —
// applying the override instantly, with no config refetch.

const OVERRIDES_KEY = 'dd.dd_flag.overrides';

// DatadogDevtools override entries use these type names, not our FlagType.
const OVERRIDE_TYPE: Record<FlagType, string> = {
  boolean: 'BOOLEAN',
  string: 'STRING',
  number: 'NUMERIC',
  object: 'JSON',
};

interface OverrideEntry {
  type: string;
  value: unknown;
}

const readRawOverrides = (): Record<string, OverrideEntry> => {
  try {
    const parsed = JSON.parse(localStorage.getItem(OVERRIDES_KEY) ?? '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return {};
};

const writeRawOverrides = (overrides: Record<string, OverrideEntry>) => {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore unavailable storage (private mode, etc.)
  }
  // Re-read into the live DatadogDevtools instance (it caches at init) + broadcast,
  // so subscribed hooks re-evaluate immediately without a network refetch.
  if (devtoolsProvider) {
    (
      devtoolsProvider as unknown as {
        overrides: Record<string, OverrideEntry>;
      }
    ).overrides = overrides;
    devtoolsProvider.events?.emit(ProviderEvents.ConfigurationChanged, {
      message: 'local flag override changed',
    });
  }
};

/** Force a flag to a local value (session-persistent via localStorage). */
export const setFlagOverride = (
  flagKey: string,
  type: FlagType,
  value: unknown,
) => {
  const overrides = readRawOverrides();
  overrides[flagKey] = { type: OVERRIDE_TYPE[type], value };
  writeRawOverrides(overrides);
};

/** Remove a single local override, reverting to the endpoint value. */
export const clearFlagOverride = (flagKey: string) => {
  const overrides = readRawOverrides();
  delete overrides[flagKey];
  writeRawOverrides(overrides);
};

/** Remove all local overrides. */
export const clearAllFlagOverrides = () => writeRawOverrides({});

/** Current local overrides as `{ flagKey: value }`. */
export const getFlagOverrides = (): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(readRawOverrides()).map(([key, entry]) => [
      key,
      entry.value,
    ]),
  );

/**
 * Every flag with a value in this environment as a `{ key: value }` map — what the
 * Datadog endpoint returned, with local overrides applied on top. Backs
 * `__RSOS.flags.list()` for console debugging.
 *
 * Not a gating tool: it carries no read-site default, so an absent key and an off
 * flag are indistinguishable. Gate on {@link getFlag} or a `use*FlagValue` hook.
 */
export const getFlags = (): Record<string, unknown> => ({
  ...Object.fromEntries(
    getResolvedFlags().map(({ key, value }) => [key, value]),
  ),
  ...getFlagOverrides(),
});

/** JS runtime type of an override value → the flag's {@link FlagType}. */
const flagTypeOf = (value: unknown): FlagType => {
  switch (typeof value) {
    case 'string':
      return 'string';
    case 'number':
      return 'number';
    case 'object':
      return 'object';
    default:
      return 'boolean';
  }
};

/**
 * Exposes `window.__RSOS.flags` for the console and Playwright — the old @rsos/flags
 * FlagProvider provided this and we still lean on it (the debug panel is the UI path;
 * this is the no-UI path). Backed by the OpenFeature override layer, so changes apply
 * live with no config refetch. `override`'s type is inferred from the value, so
 * `__RSOS.flags.override('my-flag', true)` reads the same as it always has.
 */
function exposeFlagConsoleApi(): void {
  exportToGlobalNamespace('flags', {
    override: (key: string, value: boolean | string | number | object) =>
      setFlagOverride(key, flagTypeOf(value), value),
    clear: clearFlagOverride,
    clearAll: clearAllFlagOverrides,
    list: getFlags,
  });
}
